import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import ManageModal from '../modals/ManageModal';
import CheckBox from '../CheckBox';
import InputRange from '../InputRange';
import { getError, showPrice } from 'utils/appHelpers';
import { collectProductInfo } from 'utils/projectHelpers';
import { useAlert } from 'react-alert';
import { API, graphqlOperation } from 'aws-amplify';
import { createPriceAlert, deletePriceAlert, updatePriceAlert } from 'graphql/likiees/mutations';
import { useCredits } from 'hooks/useCredits';
import './PriceAlertModal.scss';

const PriceAlertModal = ({ product, onClose, onSuccess, onRemoveSuccess }) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const history = useHistory();
  const { balance } = useCredits();
  const [fetching, setFetching] = useState(false);
  const hasPriceAlert = !!product.priceAlert;
  const frequencyOptions = {
    onceDaily: {
      name: 'Once per day',
      credits: 1,
      available: ['pro'],
      interval: 'DAILY',
      recommended: true,
    },
  };

  // Advanced frequency options (shown only when advanced settings are expanded)
  const advancedFrequencyOptions = {
    every4h: {
      name: 'Every 4 hours',
      credits: 4,
      available: ['pro'],
      interval: 'EVERY_4H',
    },
    hourly: {
      name: 'Every hour',
      credits: 8,
      available: ['pro'],
      interval: 'HOURLY',
    },
  };

  // Duration options with doubling credits (default to 1 Month)
  const durationOptions = {
    ONE_MONTH: { name: '1 Month', credits: 2, days: 30, recommended: true },
  };

  // Advanced duration options (shown only when advanced settings are expanded)
  const advancedDurationOptions = {
    ONE_WEEK: { name: '1 Week', credits: 1, days: 7 },
    THREE_MONTHS: { name: '3 Months', credits: 4, days: 90 },
  };

  // Get initial frequency from existing price alert or default to onceDaily
  const getInitialFrequency = () => {
    if (!product.priceAlert?.checkInterval) return 'onceDaily';

    // Map checkInterval back to frequency key (check both standard and advanced options)
    const allFrequencyOptions = { ...frequencyOptions, ...advancedFrequencyOptions };
    const frequencyKey = Object.keys(allFrequencyOptions).find(
      key => allFrequencyOptions[key].interval === product.priceAlert.checkInterval,
    );
    return frequencyKey || 'onceDaily';
  };

  // Get initial duration from existing price alert or default to 1 month
  const getInitialDurationKey = () => {
    if (!product.priceAlert?.duration) return 'ONE_MONTH';

    // Check if duration exists in standard options first, then advanced
    const allDurationOptions = { ...durationOptions, ...advancedDurationOptions };
    return Object.keys(allDurationOptions).includes(product.priceAlert.duration)
      ? product.priceAlert.duration
      : 'ONE_MONTH';
  };

  const [selectedFrequency, setSelectedFrequency] = useState(getInitialFrequency());
  const [selectedDuration, setSelectedDuration] = useState(getInitialDurationKey());
  const [showAdvancedDurationSettings, setShowAdvancedDurationSettings] = useState(false);
  const [showAdvancedFrequencySettings, setShowAdvancedFrequencySettings] = useState(false);

  const [form, setForm] = useState({
    alertWhenGetsCheaper: product.priceAlert?.alertWhenGetsCheaper || false,
    priceRange: product.priceAlert?.alertPriceMin || product.price || product.priceRange?.min || 0,
  });

  // Only Pro tier is available now
  const tierOptions = {
    pro: {
      name: 'Pro',
      price: 'Starting from 1 credit/month',
      credits: 1,
      features: ['Multi-site monitoring', 'Flexible frequency options', 'Advanced notifications'],
      frequency: 'onceDaily',
      creditsRequired: 1,
    },
  };

  const onFormChange = ({ target }) => {
    const { name, value } = target;
    setForm({ ...form, [name]: value });
  };

  const onCheckBoxChange = value => {
    setForm({ ...form, alertWhenGetsCheaper: value });
  };

  const onFrequencySelect = frequency => {
    // All frequencies are available for Pro tier
    setSelectedFrequency(frequency);
  };

  const onDurationSelect = durationKey => {
    setSelectedDuration(durationKey);
  };

  const getSelectedCredits = () => {
    // Calculate total credits: frequency credits * duration credits
    const allFrequencyOptions = { ...frequencyOptions, ...advancedFrequencyOptions };
    const allDurationOptions = { ...durationOptions, ...advancedDurationOptions };
    const frequencyCredits = allFrequencyOptions[selectedFrequency]?.credits || 1;
    const durationCredits = allDurationOptions[selectedDuration]?.credits || 1;
    return frequencyCredits * durationCredits;
  };

  // Calculate credit cost for button display
  const getButtonCreditCost = () => {
    const newCredits = getSelectedCredits();

    if (!hasPriceAlert) {
      // Creating new alert - show credit cost
      return newCredits;
    }

    // Editing existing alert - calculate difference
    const currentCredits = getCurrentAlertCredits();
    const creditDifference = newCredits - currentCredits;

    // If downgrading or same cost, show 0 credits
    return creditDifference > 0 ? creditDifference : 0;
  };

  // Get current alert's credit cost based on its frequency
  const getCurrentAlertCredits = () => {
    if (!product.priceAlert?.checkInterval) return 1;

    const allFrequencyOptions = { ...frequencyOptions, ...advancedFrequencyOptions };
    const currentFrequencyKey = Object.keys(allFrequencyOptions).find(
      key => allFrequencyOptions[key].interval === product.priceAlert.checkInterval,
    );

    return allFrequencyOptions[currentFrequencyKey]?.credits || 1;
  };

  // Get button text with credit information
  const getButtonText = () => {
    const creditCost = getButtonCreditCost();
    const baseText = hasPriceAlert ? t('btn_update_alert') : t('btn_create_alert');

    // Check if user has insufficient credits for new alerts
    if (!hasPriceAlert && balance !== null && balance < creditCost) {
      return 'Get Credits';
    }

    if (creditCost === 0) {
      return `${baseText} (0 credits)`;
    }

    return `${baseText} (${creditCost} credit${creditCost !== 1 ? 's' : ''})`;
  };

  // Check if user has sufficient credits
  const hasInsufficientCredits = () => {
    if (hasPriceAlert) return false; // Updates are always allowed
    const creditCost = getButtonCreditCost();
    return balance !== null && balance < creditCost;
  };

  const onFormSubmit = async () => {
    // If insufficient credits, redirect to credits page
    if (hasInsufficientCredits()) {
      history.push('/profile/subscription');
      onClose();
      return;
    }

    try {
      setFetching(true);

      // Get original price from product, handling different price structures
      let originalPrice = product.price;
      if (!originalPrice && product.priceRange && product.priceRange.min > 0) {
        originalPrice = product.priceRange.min;
      }

      // Validate that we have an originalPrice
      if (!originalPrice || originalPrice <= 0) {
        alert.error('Unable to create price alert: product price is not available');
        setFetching(false);
        return;
      }

      const body = {
        alertPriceMin: form.priceRange,
        alertWhenGetsCheaper: form.alertWhenGetsCheaper,
        originalPrice: originalPrice,
        likieeId: product.id,
        checkInterval: (frequencyOptions[selectedFrequency] || advancedFrequencyOptions[selectedFrequency]).interval,
        duration: selectedDuration,
      };

      if (hasPriceAlert) body.id = product.priceAlert.id;
      const mutation = hasPriceAlert ? updatePriceAlert : createPriceAlert;
      const { data } = await API.graphql(graphqlOperation(mutation, { input: body }));
      setFetching(false);
      onSuccess({ priceAlert: data.createPriceAlert || data.updatePriceAlert });
    } catch (err) {
      alert.error(getError(err));
      setFetching(false);
    }
  };

  const onRemovePriceAlert = async () => {
    try {
      setFetching(true);
      const body = { id: product.priceAlert.id };
      await API.graphql(graphqlOperation(deletePriceAlert, { input: body }));
      setFetching(false);
      onRemoveSuccess();
    } catch (err) {
      alert.error(getError(err));
      setFetching(false);
    }
  };

  const { mainImage } = collectProductInfo(product);

  return (
    <ManageModal
      isNewProjectCreation
      title={t('price_alarm_modal_title')}
      sumbitButton={getButtonText()}
      onCloseModal={onClose}
      onSubmit={onFormSubmit}
      isButtonDisabled={fetching}
      onRemoveItem={hasPriceAlert ? onRemovePriceAlert : null}
      className='large-modal alert-modal price-alert-modal'
    >
      <div className='row pb-5'>
        {/* Product Preview Column */}
        <div className='col-12 col-md-4'>
          <div className='product-preview'>
            <img className='product-image mb-3' width='100%' src={mainImage} alt='product' />
            <div className='current-price'>
              <p className='mb-0 text-dark fw-500'>
                {t('text_current_price')} {showPrice(product)}
              </p>
            </div>
          </div>
        </div>

        {/* Main Content Column */}
        <div className='col-12 col-md-8 mt-4 mt-md-0'>
          {/* Pro Plan Information */}
          <div className='tier-selection'>
            <h5 className='mb-3 text-dark fw-600'>Pro Plan - Price Alert Setup</h5>
            <div className='pro-plan-info'>
              <div className='tier-card pro-tier selected'>
                <div className='tier-header'>
                  <div className='tier-name'>Pro</div>
                  <div className='tier-price pro-price'>Starting from 1 credit/month</div>
                </div>
                <ul className='tier-features'>
                  {tierOptions.pro.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Duration Selection */}
          <div className='duration-selection show'>
            <div className='duration-title'>How long should we track this product?</div>
            {Object.entries(durationOptions).map(([durationKey, duration]) => (
              <div
                key={durationKey}
                className={`duration-option ${selectedDuration === durationKey ? 'selected' : ''}`}
                role='button'
                tabIndex={0}
                onClick={() => onDurationSelect(durationKey)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onDurationSelect(durationKey);
                  }
                }}
              >
                <div className='duration-name'>{duration.name}</div>
              </div>
            ))}

            {/* Advanced Duration Options */}
            {showAdvancedDurationSettings && (
              <div className='advanced-duration-options'>
                {Object.entries(advancedDurationOptions).map(([durationKey, duration]) => (
                  <div
                    key={durationKey}
                    className={`duration-option ${selectedDuration === durationKey ? 'selected' : ''}`}
                    role='button'
                    tabIndex={0}
                    onClick={() => onDurationSelect(durationKey)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onDurationSelect(durationKey);
                      }
                    }}
                  >
                    <div className='duration-name'>{duration.name}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Advanced Duration Settings Toggle */}
            <div className='show-more-toggle'>
              <button
                type='button'
                className='show-more-btn'
                onClick={() => setShowAdvancedDurationSettings(!showAdvancedDurationSettings)}
              >
                {showAdvancedDurationSettings ? 'Show less' : 'Show more'}
              </button>
            </div>
          </div>

          {/* Frequency Selection */}
          <div className='frequency-selection show'>
            <div className='frequency-title'>How often should we check the price?</div>
            {Object.entries(frequencyOptions).map(([freqKey, freq]) => (
              <div
                key={freqKey}
                className={`frequency-option ${selectedFrequency === freqKey ? 'selected' : ''}`}
                role='button'
                tabIndex={0}
                onClick={() => onFrequencySelect(freqKey)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onFrequencySelect(freqKey);
                  }
                }}
              >
                <div className='frequency-name'>{freq.name}</div>
                <div className='frequency-cost'>
                  {freq.credits *
                    (durationOptions[selectedDuration]?.credits ||
                      advancedDurationOptions[selectedDuration]?.credits ||
                      1)}{' '}
                  credit
                  {freq.credits *
                    (durationOptions[selectedDuration]?.credits ||
                      advancedDurationOptions[selectedDuration]?.credits ||
                      1) !==
                  1
                    ? 's'
                    : ''}
                </div>
              </div>
            ))}

            {/* Advanced Frequency Options */}
            {showAdvancedFrequencySettings && (
              <div className='advanced-frequency-options'>
                {Object.entries(advancedFrequencyOptions).map(([freqKey, freq]) => (
                  <div
                    key={freqKey}
                    className={`frequency-option ${selectedFrequency === freqKey ? 'selected' : ''}`}
                    role='button'
                    tabIndex={0}
                    onClick={() => onFrequencySelect(freqKey)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onFrequencySelect(freqKey);
                      }
                    }}
                  >
                    <div className='frequency-name'>{freq.name}</div>
                    <div className='frequency-cost'>
                      {freq.credits *
                        (durationOptions[selectedDuration]?.credits ||
                          advancedDurationOptions[selectedDuration]?.credits ||
                          1)}{' '}
                      credit
                      {freq.credits *
                        (durationOptions[selectedDuration]?.credits ||
                          advancedDurationOptions[selectedDuration]?.credits ||
                          1) !==
                      1
                        ? 's'
                        : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Advanced Frequency Settings Toggle */}
            <div className='show-more-toggle'>
              <button
                type='button'
                className='show-more-btn'
                onClick={() => setShowAdvancedFrequencySettings(!showAdvancedFrequencySettings)}
              >
                {showAdvancedFrequencySettings ? 'Show less' : 'Show more'}
              </button>
            </div>
          </div>

          {/* Price Settings */}
          <div className='price-settings'>
            <h6 className='mb-3 text-dark fw-600'>When should we notify you?</h6>
            <CheckBox
              className='with-border pt-7 pb-8 pr-8 pl-18 pl-md-22 mb-7'
              label={<span>When the price drops</span>}
              id='check1'
              name='alertWhenGetsCheaper'
              checked={form.alertWhenGetsCheaper}
              onChange={onCheckBoxChange.bind(null, true)}
            />
            <CheckBox
              className='with-border pt-7 pb-8 pr-8 pl-18 pl-md-22 mb-7'
              label='Set a specific price threshold (Advanced Option)'
              id='check2'
              name='alertWhenGetsCheaper'
              checked={!form.alertWhenGetsCheaper}
              onChange={onCheckBoxChange.bind(null, false)}
            />
            <InputRange
              label={t('text_price')}
              name='priceRange'
              min='0'
              max={product.price || product.priceRange?.max || product.priceRange?.min || 100}
              value={form.priceRange}
              onChange={onFormChange}
              disabled={form.alertWhenGetsCheaper}
              className='pb-10 pb-md-0'
              step='0.1'
            />
          </div>
        </div>
      </div>
    </ManageModal>
  );
};

export default PriceAlertModal;
